import { useEffect, useState, type KeyboardEvent } from 'react';
import { RevenueTransactionDetails } from './RevenueTransactionDetails';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import { api } from '@/lib/api-client';
import { DataTable } from '@/components/data-display/DataTable';
import {
  RevenueTransactionsSchema,
  formatRevenueAmount,
  type RevenueTransaction,
} from './transaction-types';
import type { RevenueDateRange } from './types';

const labels = { payment: 'Outgoing', incoming: 'Incoming', conversion: 'Conversion' };
const badges = {
  payment: 'bg-[#fce4ec] text-[#c62828]',
  incoming: 'bg-[#e3f2fd] text-[#1565c0]',
  conversion: 'bg-[#faf4ff] text-[#7b1fa2]',
};
const money = (amount: string, currency: string) => `${formatRevenueAmount(amount)} ${currency}`;
const columns: ColumnDef<RevenueTransaction, unknown>[] = [
  {
    accessorKey: 'created_at',
    header: 'Date',
    cell: ({ row }) => (
      <time dateTime={row.original.created_at} className="text-[#666]">
        {new Date(row.original.created_at).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </time>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <span
        className={`inline-block max-w-full rounded-full px-2 py-1 text-[11px] font-semibold ${badges[row.original.type]}`}
      >
        {labels[row.original.type]}
      </span>
    ),
  },
  {
    accessorKey: 'transaction_cod',
    header: 'Transaction Code',
    cell: ({ row }) => (
      <span
        className="block break-words font-mono text-xs"
        title={row.original.transaction_id ?? undefined}
      >
        {row.original.transaction_cod ?? '—'}
      </span>
    ),
  },
  {
    accessorKey: 'client_name',
    header: 'Client',
    cell: ({ row }) => <span className="break-words font-medium">{row.original.client_name}</span>,
  },
  {
    id: 'operation',
    header: 'Operation',
    cell: ({ row: { original: r } }) => {
      const description =
        r.type === 'conversion'
          ? `Conversion ${money(r.amount, r.currency_code)} → ${r.buy_amount !== null && r.buy_currency !== null ? money(r.buy_amount, r.buy_currency) : '—'}`
          : `${labels[r.type]} ${money(r.amount, r.currency_code)}`;
      return (
        <span className="block max-w-[260px] truncate text-[#666]" title={description}>
          {description}
        </span>
      );
    },
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => (
      <span className="block break-words text-right font-medium tabular-nums">
        {money(row.original.amount, row.original.currency_code)}
      </span>
    ),
  },
  {
    accessorKey: 'revenue',
    header: 'Revenue',
    cell: ({ row: { original: r } }) => (
      <div className="text-right">
        <span
          className={`break-words font-bold tabular-nums ${r.revenue.startsWith('-') ? 'text-[#c62828]' : 'text-[#2e7d32]'}`}
        >
          {r.revenue.startsWith('-') ? '' : '+'}
          {money(r.revenue, r.revenue_currency)}
        </span>
        <span className="mt-0.5 block text-xs text-[#999]">
          {r.type === 'conversion' ? 'spread' : 'fee'}
        </span>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span
        className={`font-semibold ${row.original.status === 'completed' ? 'text-[#2e7d32]' : 'text-[#1565c0]'}`}
      >
        {row.original.status}
      </span>
    ),
  },
];
columns.forEach((column) => {
  column.enableSorting = false;
});

export function RevenueTransactions({ dateRange }: { dateRange: RevenueDateRange }) {
  const [selectedTransaction, setSelectedTransaction] = useState<RevenueTransaction | null>(null);
  useEffect(() => {
    const closeDetails = () => setSelectedTransaction(null);
    window.addEventListener('cbs:tenant-switched', closeDetails);
    return () => window.removeEventListener('cbs:tenant-switched', closeDetails);
  }, []);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 1, pageSize: 25 });
  const query = useQuery({
    queryKey: ['revenue-transactions', dateRange, pagination],
    queryFn: async () =>
      RevenueTransactionsSchema.parse(
        await api.get<unknown>('/api/revenue/transactions', {
          params: { ...dateRange, page: pagination.pageIndex, page_size: pagination.pageSize },
        }),
      ),
  });
  return (
    <section data-testid="revenue-transactions" aria-label="Transaction revenue">
      <h2 className="mb-1 text-lg font-bold text-[#1a1a1a]">Transaction Revenue</h2>
      <p className="mb-4 text-[13px] text-[#999]">
        Incoming and outgoing fees and conversion margins, in their recorded currencies.
      </p>
      {query.error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] p-4 text-sm text-[#c62828]"
        >
          Unable to load transaction revenue.{' '}
          <button className="font-semibold underline" onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <div>
          <DataTable
            columns={columns}
            data={query.data?.items ?? []}
            totalRows={query.data?.total ?? 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            isLoading={query.isFetching}
            rowClick={setSelectedTransaction}
            getRowProps={(row) => ({
              tabIndex: 0,
              'aria-label': `View ${labels[row.type]} ${row.transaction_cod ?? row.transaction_id ?? row.id} details`,
              className:
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6B0F1A]',
              onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedTransaction(row);
                }
              },
            })}
            tableClassName="min-w-[800px] table-fixed [&_td]:px-2 [&_th]:px-2 [&_th:nth-child(1)]:w-[10%] [&_th:nth-child(2)]:w-[9%] [&_th:nth-child(3)]:w-[14%] [&_th:nth-child(4)]:w-[12%] [&_th:nth-child(5)]:w-[19%] [&_th:nth-child(6)]:w-[12%] [&_th:nth-child(7)]:w-[14%] [&_th:nth-child(8)]:w-[10%]"
          />
        </div>
      )}
      <RevenueTransactionDetails
        transaction={selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />
    </section>
  );
}
